<script lang="ts">
import { defineComponent, PropType, inject, ref, computed, watch } from 'vue'
import ERROR_TYPES from '@/constants/errorTypes'
import { VALIDATION_MESSAGE_TYPES, VALIDATION_DEFAULT_MESSAGES } from '@/constants/validationMessages'
import {
  isValidEmail,
  isValidMailDomain,
  isValidURL,
  isValidPassword,
  isValidNumber,
  isValidKana,
  isValidDate,
  isValidPhoneNumberFormat,
  isValidPhoneNumber,
  isInputed,
  isUnderMaxLength,
  isOverMinLength,
} from '@/utilities/validator'
import { useErrorStore } from '@/stores/error'
import { useValidationStore } from '@/stores/validation'
import { useToastStore } from '@/stores/toast'
import { hasOwnProperty } from '@/utilities/syntaxSugar'

export default defineComponent({
  name: 'ValidationProvider',

  props: {
    /**
     * 検査対象の value
     */
    value: {
      type: [String, Number, Boolean, Array, File] as PropType<InputValue | undefined>,
      default: undefined,
    },

    /**
     * チェックボックス等の選択形式のフォームにおいて、未選択時の value が明示的に存在する場合の未選択値
     * @description チェックボックス等は、false-value オプションが付与できる（未選択時の値）。未選択時にも明示的に値が設定されるものに関しては、未選択 = 空欄 にはならないため、required 判定が抜けてしまうため、false value との比較を行う。
     */
    falseValue: {
      type: [String, Number, Boolean],
      default: undefined,
    },

    /**
     * バリデーションの名前空間
     * @description 自身に紐づく名前空間のバリデーションコールにのみ反応する。
     * @description 値は任意であるが、store に登録されるバリデーションキューの namespace の値と同一にする必要がある。
     * @description Form submit の場合、form の name 属性の値にする。
     */
    namespace: {
      type: String,
      default: '',
    },

    /**
     * store にエラーメッセージを登録する際のフィールド名
     */
    field: {
      type: String,
      default: '',
    },

    /**
     * 有効にするバリデーションの設定
     */
    scheme: {
      type: Object as PropType<ValidationScheme>,
      default: () => ({
        /**
         * validation scheme examples
         */
        required: false,
        email: false,
        url: false,
        password: false,
        number: false,
        kana: false,
        date: false,
        phoneNumber: false,
        maxLength: 0,
        minLength: 0,
      }),
    },

    /**
     * バリデーションエラー時に表示するメッセージ定義
     * @description 定義を注入しなかった場合、デフォルトの文言が表示される。
     */
    messages: {
      type: Object as PropType<ValidationMessages>,
      default: () => ({
        ...VALIDATION_DEFAULT_MESSAGES,
      }),
    },

    /**
     * エラーメッセージを表示するかどうか
     *
     * @description
     *  バリデーションはしたいが、エラーメッセージは隠したい場合（hidden 要素等）、
     *  特に、「勤務先名」のように入力フィールドに付随する hidden 要素が存在する場合において、入力フィールドの入力状況によって hidden 要素の方のエラーを表示するかどうか制御するような状況で使用する。
     *  バリデーションエラーメッセージのアニメーションの兼ね合いで DOM が存在していないといけないため、v-if 制御ではなく、CSS 側での表示制御とする
     */
    showMessage: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['error', 'start', 'completed'],

  setup(props, { emit }) {
    // constants
    const INVALID_MAIL_DOMAINS = inject<string[]>('INVALID_MAIL_DOMAINS') || []

    const errorStore = useErrorStore()
    const validationStore = useValidationStore()
    const toastStore = useToastStore()


    const isError = ref(false)

    /**
     * バリデーションが設定されているかどうか
     */
    const hasValidation = computed<boolean>(() => {
      if (!props.scheme || !Object.keys(props.scheme).length) return false

      // validation scheme の各設定に一つでも true、もしくは数値（maxLength 等）が入っていたらバリデーションあり
      return Object.values(props.scheme).some(v => !!v)
    })

    /**
     * 自身の関係あるバリデーションかどうかは関係なく、
     * 画面内でバリデーションが発火したタイミングで一旦画面内のすべてのエラーをリセットする
     */
    watch(() => validationStore.context.observe, (newValue, oldValue) => {
      if (!oldValue && newValue) isError.value = false
    })

    /**
     * バリデーションキューを監視し、自身のキューが登録され run フラグが立ったらバリデーション発火
     */
    watch(() => validationStore.getQueue(props.namespace)?.run, async (newValue) => {
      if (newValue) {
        if (validationStore.checkQueueStacking(props.namespace) && hasValidation.value) {
          emit('start')

          if (await validate()) {
            validationStore.valid(props.namespace, props.field)
          } else {
            isError.value = true
            emit('error', props.field)
          }

          validationStore.completed(props.namespace, props.field)
          emit('completed', props.field)
        }
      }
    })

    /**
     * 各種バリデーション
     * @description コンポーネントの呼び出し元から props で渡されたバリデーション設定（scheme）のうち、有効になっているもののみを行います。
     * @description バリデーションに引っかかった段階で判定は終了されます。（引っかかった段階でそれ以降のバリデーションは走査されません。）
     * @see @/utilities/validator.ts
     */
    const validate = async (): Promise<boolean> => {
      isError.value = false

      let message = ''

      // 必須チェック
      if (props.scheme.required) {
        // チェックボックス系で false value に指定がある場合は、入力値と false value との比較（それ以外は、単純に値が入っているかのチェック）
        const isValid = props.falseValue !== undefined
          ? props.value !== props.falseValue
          : isInputed(props.value)

        if (!isValid) {
          message = hasOwnProperty(props.messages, 'required') && props.messages.required
            ? props.messages.required
            : VALIDATION_MESSAGE_TYPES.INPUT_REQUIRED
          addValidationErrorMessage(props.field, message)
          return false
        }
      }

      if (isInputed(props.value)) {
        // Email 入力形式チェック
        if (props.scheme.email) {
          if (!isValidEmail(props.value!.toString())) {
            message = props.messages.email
              ? props.messages.email
              : VALIDATION_MESSAGE_TYPES.INVALID_EMAIL
            addValidationErrorMessage(props.field, message)
            return false
          }
        }

        // メールドメインチェック
        if (props.scheme.mailDomain) {
          if (!isValidMailDomain(INVALID_MAIL_DOMAINS, props.value!.toString())) {
            message = props.messages.mailDomain
              ? props.messages.mailDomain
              : VALIDATION_MESSAGE_TYPES.INVALID_MAIL_DOMAIN
            addValidationErrorMessage(props.field, message)
            return false
          }
        }

        // URL 入力形式チェック
        if (props.scheme.url) {
          if (!isValidURL(props.value!.toString())) {
            message = props.messages.url
              ? props.messages.url
              : VALIDATION_MESSAGE_TYPES.INVALID_URL
            addValidationErrorMessage(props.field, message)
            return false
          }
        }

        // パスワード入力形式チェック
        if (props.scheme.password) {
          if (!isValidPassword(props.value!.toString())) {
            message = props.messages.password
              ? props.messages.password
              : VALIDATION_MESSAGE_TYPES.INVALID_PASSWORD
            addValidationErrorMessage(props.field, message)
            return false
          }
        }

        // 半角数字入力チェック
        if (props.scheme.number) {
          if (!isValidNumber(props.value!.toString())) {
            message = props.messages.number
              ? props.messages.number
              : VALIDATION_MESSAGE_TYPES.INVALID_NUMBER
            addValidationErrorMessage(props.field, message)
            return false
          }
        }

        // かな入力形式チェック
        if (props.scheme.kana) {
          if (!isValidKana(props.value!.toString())) {
            message = props.messages.kana
              ? props.messages.kana
              : VALIDATION_MESSAGE_TYPES.INVALID_KANA
            addValidationErrorMessage(props.field, message)
            return false
          }
        }

        // 日付入力形式チェック
        if (props.scheme.date) {
          if (!isValidDate(props.value!.toString())) {
            message = props.messages.date
              ? props.messages.date
              : VALIDATION_MESSAGE_TYPES.INVALID_DATE
            addValidationErrorMessage(props.field, message)
            return false
          }
        }

        // 電話番号入力形式チェック
        if (props.scheme.phoneNumber) {
          // 個別でバリデーションとして弾きたい電話番号
          const phoneNumberBlacklist = [
            '09012345678',
          ]

          const addValidationErrorMessageForPhoneNumber = () => {
            message = props.messages.phoneNumber
              ? props.messages.phoneNumber
              : VALIDATION_MESSAGE_TYPES.INVALID_PHONE_NUMBER
            addValidationErrorMessage(props.field, message)
          }

          const toastItem: ToastItem = {
            id: `toast-${new Date().getTime()}`,
            messageType: 'alert',
            message: '',
          }

          // 除外リストに含まれる電話番号かどうか
          if (phoneNumberBlacklist.includes(props.value!.toString())) {
            addValidationErrorMessageForPhoneNumber()

            toastItem.message = '許可されない電話番号です'
            toastStore.setToastItem(toastItem)

            return false
          }

          // 電話番号フォーマットチェック
          if (!isValidPhoneNumberFormat(props.value!.toString())) {
            addValidationErrorMessageForPhoneNumber()

            toastItem.message = '半角数字のみで番号を入力してください'
            toastStore.setToastItem(toastItem)

            return false
          }

          // 電話番号有効性チェック（Twilio API）
          const isValid = await isValidPhoneNumber(props.value!.toString())
          if (!isValid) {
            addValidationErrorMessageForPhoneNumber()

            toastItem.message = '有効な電話番号ではありません'
            toastStore.setToastItem(toastItem)

            return false
          }
        }

        // 文字数範囲チェック
        if ((props.scheme.maxLength && props.scheme.maxLength > 0) && (props.scheme.minLength && props.scheme.minLength > 0)) {
          let max = 0
          let min = 0

          // props で指定された「最大」「最小」の値の大小が逆の場合は「最小」で指定された値を max として、「最大」で指定された値を min として扱う
          if (props.scheme.maxLength >= props.scheme.minLength) {
            max = props.scheme.maxLength
            min = props.scheme.minLength
          } else {
            max = props.scheme.minLength
            min = props.scheme.maxLength
          }

          if (!isUnderMaxLength(props.value!.toString(), max) || !isOverMinLength(props.value!.toString(), min)) {
            message = props.messages.range
              ? props.messages.range
              : VALIDATION_MESSAGE_TYPES.OUT_OF_RANGE
                .replace(/{{ min }}/, `${min}文字`)
                .replace(/{{ max }}/, `${max}文字`)
            addValidationErrorMessage(props.field, message)
            return false
          }
        }

        // 最大文字数チェック
        if (props.scheme.maxLength && props.scheme.maxLength > 0) {
          if (!isUnderMaxLength(props.value!.toString(), props.scheme.maxLength)) {
            message = props.messages.maxLength
              ? props.messages.maxLength
              : VALIDATION_MESSAGE_TYPES.OVER_MAX_LENGTH
            addValidationErrorMessage(props.field, message)
            return false
          }
        }

        // 最小文字数チェック
        if (props.scheme.minLength && props.scheme.minLength > 0) {
          if (!isOverMinLength(props.value!.toString(), props.scheme.minLength)) {
            message = props.messages.minLength
              ? props.messages.minLength
              : VALIDATION_MESSAGE_TYPES.BELOW_MIN_LENGTH
            addValidationErrorMessage(props.field, message)
            return false
          }
        }
      }

      // すべてのバリデーション通過
      return true
    }

    /**
     * バリデーションエラーメッセージの store への登録
     * @param field store に登録する際の field 名（基本的に、サーバ側の対応するプロパティの field 名か、渡されてきた name 属性を指定）。バリデーションメッセージコンポーネント側が、props で渡された field 名と同名の filed プロパティを持ったオブジェクトを store から探索し、表示する仕組み。
     * @param message セットするエラーメッセージ本文
     */
    const addValidationErrorMessage = (field: string, message: string) => {
      const errorItem: ErrorItem = {
        field: field,
        messages: [],
      }

      errorItem.messages = [message]
      errorStore.setError(ERROR_TYPES.VALIDATION_ERROR, errorItem)
    }

    return {
      //store
      errorStore,

      // data
      isError,
    }
  }
})
</script>

<template>
  <div
    class="l-unit__validationProvider"
    :data-validation-error="!!errorStore.getErrorMessagesByFieldName(field).length">
    <div class="l-unit__validationProvider--description">
      <slot />

      <FieldErrorMessage
        v-if="$slots.default"
        :field="field"
        :show-message="showMessage" />

      <slot name="secondary" />
    <!-- /.l-unit__validationProvider description --></div>
  <!-- /.l-unit__validationProvider --></div>
</template>
